import { createSlice } from '@reduxjs/toolkit';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
import webServices from '../../services/webServices';

// ----------------------------------------------------------------------
// "Service performed" log (Homeowner card 84). A service record is a maintenance
// event a homeowner logs against one of their existing items.

export const SERVICE_ACTIONS = ['Clean', 'Install', 'Repair', 'Routine Maintenance'];

const initialState = {
  isLoading: false,
  isDeleteLoading: false,
  isLoadingBtnUpld: false,
  error: false,
  success: '',
  serviceRecordList: [],
  itemList: [],
  companyList: []
};

const slice = createSlice({
  name: 'serviceRecord',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    startDeleteLoading(state) {
      state.isDeleteLoading = true;
    },
    startLoadingUpld(state) {
      state.isLoadingBtnUpld = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isDeleteLoading = false;
      state.isLoadingBtnUpld = false;
      state.error = action.payload;
    },
    getServiceRecordListSuccess(state, action) {
      state.isLoading = false;
      state.serviceRecordList = action.payload;
      state.success = '';
      state.isDeleteLoading = false;
    },
    getItemListSuccess(state, action) {
      state.itemList = action.payload;
    },
    getCompanyListSuccess(state, action) {
      state.companyList = action.payload;
    },
    actionSuccess(state, action) {
      state.isLoading = false;
      state.success = action.payload;
      state.isLoadingBtnUpld = false;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {
  hasError,
  startLoading,
  startDeleteLoading,
  startLoadingUpld,
  getServiceRecordListSuccess,
  getItemListSuccess,
  getCompanyListSuccess,
  actionSuccess
} = slice.actions;

// ----------------------------------------------------------------------

// Items the homeowner can log a service against (populates the Item picker).
export function getServiceItems() {
  return async (dispatch) => {
    try {
      const resp = await webServices.getServiceRecordItems();
      if (resp && resp.success === true) {
        dispatch(getItemListSuccess(resp.data));
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

// Existing companies for the Company picker (free text also allowed).
export function getCompanies() {
  return async (dispatch) => {
    try {
      const resp = await webServices.getCompany();
      if (resp && resp.success === true) {
        dispatch(getCompanyListSuccess(resp.data));
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function getServiceRecords() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const resp = await webServices.getServiceRecords();
      if (resp && resp.success === true) {
        dispatch(getServiceRecordListSuccess(resp.data));
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function createServiceRecord(data, navigate) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const resp = await webServices.createServiceRecord(data);
      if (resp && resp.success === true) {
        dispatch(actionSuccess('Create success'));
        navigate(PATH_DASHBOARD.general.services);
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function modifyServiceRecord(data, navigate, previousData) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const { id, image } = previousData;
      if (data.image && typeof data.image === 'object') {
        data.oldImage = image;
      }
      delete data.otherFiles;
      const resp = await webServices.updateServiceRecord(id, data);
      if (resp && resp.success === true) {
        dispatch(actionSuccess('Modify success'));
        navigate(PATH_DASHBOARD.general.services);
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function deleteServiceRecord(navigate, id) {
  return async (dispatch) => {
    dispatch(startDeleteLoading());
    try {
      const resp = await webServices.softDeleteServiceRecord(id, { isDeleted: true });
      if (resp && resp.success === true) {
        dispatch(actionSuccess('Delete success'));
        navigate(PATH_DASHBOARD.general.services);
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function uploadFiles(otherFiles, id, navigate) {
  return async (dispatch) => {
    dispatch(startLoadingUpld());
    try {
      const resp = await webServices.fileUploadServiceRecord(id, { otherFiles });
      if (resp && resp.success === true) {
        dispatch(actionSuccess('Uploaded success'));
        dispatch(actionSuccess(''));
        const result = resp.data;
        result.id = result._id;
        navigate(PATH_DASHBOARD.general.servicesEdit, { state: result });
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function deleteFiles(fileURL, id, navigate) {
  return async (dispatch) => {
    try {
      const resp = await webServices.fileDeleteServiceRecord(id, { fileURL });
      if (resp && resp.success === true) {
        dispatch(actionSuccess('Deleted success'));
        dispatch(actionSuccess(''));
        const result = resp.data;
        result.id = result._id;
        navigate(PATH_DASHBOARD.general.servicesEdit, { state: result });
      } else {
        dispatch(hasError(resp.message));
      }
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}
